/**
 * A4 — Notification wire schemas (Notification :5400), Zod-parsed at the seam.
 * Mirrors InboxItemDto / TemplateDto / PreferenceDto field-by-field.
 */
import { z } from 'zod';

import { pagedListSchema } from './education';

const isoDate = z.string().min(1);

export const inboxItemSchema = z.object({
  id: z.guid(),
  title: z.string(),
  body: z.string(),
  category: z.string(),
  audience: z.string(),
  isRead: z.boolean(),
  readAtUtc: isoDate.nullish(),
  createdAtUtc: isoDate
});

export const notificationTemplateSchema = z.object({
  id: z.guid(),
  key: z.string(),
  name: z.string(),
  channel: z.string(),
  version: z.number(),
  subject: z.string(),
  body: z.string(),
  /**
   * MAIL-HTML (backend mig 443) — the authored HTML part, email channel only.
   *
   * ⚠ NULLABLE, NOT OPTIONAL-STRING. The column is null on every template that predates the
   * migration, which today is all 49 of them. A required string here would throw on each one, and a
   * list schema that throws blanks the WHOLE page rather than one field.
   *
   * This is the SANITIZED markup the server stored, which is not byte-identical to what was posted —
   * the allowlist rewrites as well as strips (`#fff` becomes `rgba(...)`, a `tbody` is injected). That
   * is deliberate: what the editor loads back is what will actually be sent.
   */
  bodyHtml: z.string().nullish(),
  isActive: z.boolean(),
  /**
   * TPL-MANAGE — is this the version that would actually be sent right now?
   *
   * ⚠ `isActive` IS NOT THIS. Active means "not retired", and several versions of one key can be
   * active at once; the send path takes the HIGHEST active one, so exactly one is live. Rendering
   * every active row as equally live is what let two test versions sit on top of a tenant's real
   * invoice template looking interchangeable with it.
   *
   * Defaulted rather than required so a page served by a pre-TPL-MANAGE backend degrades to "nothing
   * marked live" instead of throwing — a list schema that throws blanks the whole page.
   */
  isLive: z.boolean().default(false),
  /**
   * TPL-LIST — how many versions this key+channel has, including retired ones.
   *
   * The list returns ONE row per template now, so this is the only sign from there that a template
   * has a history at all. Defaulted for the same reason as `isLive`: an older backend omits it, and a
   * list schema that throws blanks the whole page.
   */
  versionCount: z.number().default(1),
  createdAtUtc: isoDate
});

/**
 * TPL-MANAGE — one key an author may choose, from Notification's own recipe catalog.
 *
 * ⚠ NOT the full set of a tenant's keys. Event-reminder rules point at `notif.templates.key` through
 * an opaque, FK-less column invented by whoever wrote the rule, so any picker built on this must
 * still accept a typed key.
 */
export const templateCatalogEntrySchema = z.object({
  key: z.string(),
  channels: z.array(z.string()),
  category: z.string(),
  eventTypes: z.array(z.string()),
  /** The subset of `channels` that already has an active template. The difference is a mail with nowhere to render from. */
  channelsWithTemplate: z.array(z.string())
});

/**
 * MAIL-LAYOUT (backend mig 445) — the shared shell every HTML email is composed into.
 *
 * ⚠ `bodySlot` comes FROM THE SERVER rather than being a constant here. The editor, its preview and
 * the consumer must all agree on that one string; a second copy in TypeScript is a copy that can
 * drift, and the failure it produces is a shell that silently swallows every message.
 */
export const mailLayoutSchema = z.object({
  layoutHtml: z.string(),
  updatedAtUtc: isoDate,
  bodySlot: z.string()
});

export type MailLayout = z.infer<typeof mailLayoutSchema>;

export const templateCatalogSchema = z.array(templateCatalogEntrySchema);
export type TemplateCatalogEntry = z.infer<typeof templateCatalogEntrySchema>;

/**
 * NotificationChannels.All (backend Domain) — per-channel opt-out targets. Değerler backend'in
 * kanal sabitleriyle BİREBİR aynı olmalı (PUT /preferences/{channel} validator'ı
 * NotificationChannels.All'a göre doğrular) — 'inapp' (alt çizgisiz), 'in_app' DEĞİL.
 */
export const notificationChannels = ['email', 'inapp', 'sms', 'push'] as const;
export type NotificationChannel = (typeof notificationChannels)[number];

/**
 * §6.6 onay kategorileri. '*' = tüm kategoriler; security AYARLANAMAZ (hep teslim) → listede yok.
 * Backend NotificationCategories.Settable ile birebir.
 */
export const consentCategories = ['tenant_lifecycle', 'billing', 'system'] as const;
export type ConsentCategory = (typeof consentCategories)[number];

export const notificationPreferenceSchema = z.object({
  category: z.string(),
  channel: z.string(),
  optedOut: z.boolean(),
  updatedAtUtc: isoDate
});

export const notificationPreferenceListSchema = z.array(notificationPreferenceSchema);

/** PreferenceDefaultDto — tenant varsayılan onayı (§6.6, admin). */
export const preferenceDefaultSchema = z.object({
  category: z.string(),
  channel: z.string(),
  optedOut: z.boolean(),
  updatedAtUtc: isoDate
});
export const preferenceDefaultListSchema = z.array(preferenceDefaultSchema);

/** DigestFrequencies.All (backend Domain) — push/email digest sıklığı. */
export const digestFrequencies = ['off', 'daily'] as const;
export type DigestFrequency = (typeof digestFrequencies)[number];

/**
 * DeliveryPrefsDto — #14 sessiz saatler + digest (çağıranın KENDİ teslim tercihleri).
 * Kanal/kategori opt-out'tan AYRI: bildirimi susturmaz, yalnız gürültülü kanalların
 * (push/email) ZAMANINI kaydırır. Dakikalar gün-içi dakika (0–1439), yerel saat.
 */
export const deliveryPrefsSchema = z.object({
  quietEnabled: z.boolean(),
  quietStartMinute: z.number().int().min(0).max(1439),
  quietEndMinute: z.number().int().min(0).max(1439),
  digestFrequency: z.enum(digestFrequencies),
  digestHourMinute: z.number().int().min(0).max(1439),
  timeZone: z.string(),
  updatedAtUtc: isoDate
});
export type DeliveryPrefs = z.infer<typeof deliveryPrefsSchema>;

export const inboxListSchema = pagedListSchema(inboxItemSchema);
export const notificationTemplateListSchema = pagedListSchema(notificationTemplateSchema);

/** InboxUnreadCountResult — GET /notifications/inbox/unread-count (rozet). */
export const inboxUnreadCountSchema = z.object({ unread: z.number() });

/** MarkAllReadResult — POST /notifications/inbox/read-all. */
export const inboxMarkAllReadSchema = z.object({ marked: z.number() });

export type InboxItem = z.infer<typeof inboxItemSchema>;
export type NotificationTemplate = z.infer<typeof notificationTemplateSchema>;
export type NotificationPreference = z.infer<typeof notificationPreferenceSchema>;
export type PreferenceDefault = z.infer<typeof preferenceDefaultSchema>;

// P4-3 Integrations Hub — giden webhook uç + teslimat şemaları. secret ASLA dönmez.
export const webhookDeliveryStatuses = ['pending', 'delivering', 'delivered', 'failed', 'dead'] as const;

export const webhookEndpointSchema = z.object({
  id: z.guid(),
  name: z.string(),
  url: z.string(),
  eventTypes: z.array(z.string()),
  active: z.boolean(),
  createdAt: isoDate
});

export const webhookDeliverySchema = z.object({
  id: z.guid(),
  endpointId: z.guid(),
  eventType: z.string(),
  status: z.enum(webhookDeliveryStatuses),
  attempts: z.number(),
  maxAttempts: z.number(),
  lastStatusCode: z.number().nullish(),
  deliveredAt: isoDate.nullish(),
  createdAt: isoDate
});

export const webhookDeliveryListSchema = pagedListSchema(webhookDeliverySchema);

export type WebhookEndpoint = z.infer<typeof webhookEndpointSchema>;
export type WebhookDelivery = z.infer<typeof webhookDeliverySchema>;
export type WebhookDeliveryStatus = (typeof webhookDeliveryStatuses)[number];

// P4-4 Discuss — REST sohbet şemaları. Mesaj AI moderasyon durumu taşır [STD-AI];
// assistant cevabı ragSources (jsonb) ile gelir. author_ref/user_ref OPAK.
export const discussChannelKinds = ['direct', 'group', 'support', 'assistant'] as const;
export const discussMessageKinds = ['user', 'assistant', 'system'] as const;
export const discussModerationStatuses = ['clean', 'flagged', 'blocked'] as const;

export const discussChannelSchema = z.object({
  id: z.guid(),
  name: z.string(),
  kind: z.enum(discussChannelKinds),
  createdAt: isoDate
});

export const discussMemberSchema = z.object({
  userRef: z.guid(),
  role: z.string(),
  joinedAt: isoDate
});

export const discussChannelDetailSchema = z.object({
  channel: discussChannelSchema,
  members: z.array(discussMemberSchema)
});

export const discussMessageSchema = z.object({
  id: z.guid(),
  authorRef: z.guid().nullish(),
  kind: z.enum(discussMessageKinds),
  body: z.string(),
  moderationStatus: z.enum(discussModerationStatuses),
  ragSources: z.string().nullish(),
  createdAt: isoDate
});

export const discussMessageListSchema = pagedListSchema(discussMessageSchema);

export type DiscussChannel = z.infer<typeof discussChannelSchema>;
export type DiscussMember = z.infer<typeof discussMemberSchema>;
export type DiscussChannelDetail = z.infer<typeof discussChannelDetailSchema>;
export type DiscussMessage = z.infer<typeof discussMessageSchema>;
export type DiscussModerationStatus = (typeof discussModerationStatuses)[number];

// ============================================================================
// MAIL-CRED (mig 303) — the tenant's own outgoing mail server, from the admin
// Integrations screen. Mirrors EmailCredentialStatusDto / EmailCredentialTestDto /
// SendTestEmailResultDto field-by-field.
//
// The password is NEVER in any of these shapes: the server writes it and never reads
// it back, so the form starts empty on every open and a blank password PRESERVES the
// stored one. passwordLast4 exists only so an admin can tell WHICH credential is saved.
// ============================================================================

/** Mirrors SmtpEncryptionModes. */
export const smtpEncryptionModes = ['none', 'starttls', 'ssl'] as const;

/**
 * What the admin may choose — now ALL THREE.
 *
 * 'ssl' (implicit TLS, usually port 465) was excluded while the server used System.Net.Mail, which has
 * no implicit-TLS mode at all: the probe could still test 465 green, so offering it would have meant a
 * passing connection test beside mail that never arrives. The MailKit switch removed that asymmetry, so
 * a 465-only provider is reachable.
 *
 * ⚠ 'starttls' is what System.Net.Mail called `EnableSsl = true`. That flag never meant implicit TLS,
 * which is why "enable SSL" and "port 465" are not the same request.
 */
export const selectableSmtpEncryptionModes = ['starttls', 'ssl', 'none'] as const;

/** Mirrors EmailDeliveryModes — what is ACTUALLY sending mail right now. */
export const emailDeliveryModes = ['tenant', 'platform', 'log-only'] as const;

export const emailCredentialStatusSchema = z.object({
  configured: z.boolean(),
  provider: z.string(),
  isActive: z.boolean(),
  host: z.string(),
  port: z.number(),
  encryption: z.string(),
  username: z.string().nullish(),
  passwordSet: z.boolean(),
  passwordLast4: z.string().nullish(),
  fromAddress: z.string(),
  fromDisplayName: z.string().nullish(),
  replyToAddress: z.string().nullish(),
  lastSuccessAtUtc: isoDate.nullish(),
  lastErrorAtUtc: isoDate.nullish(),
  lastError: z.string().nullish(),
  updatedAtUtc: isoDate.nullish(),
  // Not an enum(): an older/newer server must not fail the parse over a mode this build
  // does not know. The card falls back to treating an unknown value as "not configured".
  effectiveMode: z.string()
});

/** Handshake result. ok=true with tlsUsed=false is a REAL state — see insecureWarning. */
export const emailCredentialTestSchema = z.object({
  ok: z.boolean(),
  greeting: z.string().nullish(),
  tlsUsed: z.boolean(),
  startTlsOffered: z.boolean(),
  authenticated: z.boolean(),
  elapsedMs: z.number(),
  failedStep: z.string().nullish(),
  error: z.string().nullish(),
  insecureWarning: z.boolean()
});

/** Result of delivering one real test message. A rejected send is DATA, not an HTTP error. */
export const sendTestEmailResultSchema = z.object({
  sent: z.boolean(),
  recipient: z.string(),
  error: z.string().nullish()
});

/** PUT /notifications/email-credentials. password null/blank = keep the stored one. */
export interface EmailCredentialRequest {
  host: string;
  port: number;
  encryption: string;
  username: string | null;
  password: string | null;
  fromAddress: string;
  fromDisplayName: string | null;
  replyToAddress: string | null;
  isActive: boolean;
}

export type EmailCredentialStatus = z.infer<typeof emailCredentialStatusSchema>;
export type EmailCredentialTest = z.infer<typeof emailCredentialTestSchema>;
export type SendTestEmailResult = z.infer<typeof sendTestEmailResultSchema>;
export type SmtpEncryptionMode = (typeof smtpEncryptionModes)[number];
export type EmailDeliveryMode = (typeof emailDeliveryModes)[number];
